import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSpin } from '../../components/SpinContext';
import { CustomButton, CustomInput, ShowMessage } from '../../components';
import { Col, Flex, Form, message, Row } from 'antd';
import { ForgotPasswordApi } from '../../apis/auth';
import CheckMarkIcon from '../../assets/images/check-mark.png';
import { getThemeAccentColor } from '@/utils/themeColor';

interface ForgotPasswordFormValues {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const { showSpin, hideSpin } = useSpin();
  const [form] = Form.useForm<ForgotPasswordFormValues>();
  const navigate = useNavigate();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const themeColor = getThemeAccentColor();
  const btnColor = getThemeAccentColor();

  const handleForgotPassword = (values: ForgotPasswordFormValues): void => {
    showSpin();
    const { email } = values;
    ForgotPasswordApi(email)
      .then((res) => {
        if (res?.data?.data.is_success) {
          setEmailSent(true);
          message.success('A reset link has been sent to your email.');
        } else {
          message.error(
            res?.data?.data.message || 'Something went wrong. Try again.'
          );
        }
      })
      .catch((error: unknown) => {
        console.error('Error:', error);
        message.error('Failed to send reset link. Please try again later.');
      })
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  return (
    <>
      <div className='forgot-pass'>
        {emailSent ? (
          <Flex
            vertical
            align='center'
            justify='center'
            style={{ height: 'inherit' }}
          >
            <ShowMessage
              heading='Email Sent Successfully'
              icon={CheckMarkIcon}
              text='Check your inbox for the password reset link.'
              themeColor={themeColor}
            />
            <CustomButton
              text='Back to Login'
              block={true}
              height='48px'
              bgColor={btnColor}
              color='#fff'
              borderRadius='8px'
              fontSize='16px'
              fontWeight={500}
              onClick={() => navigate('/login')}
            />
          </Flex>
        ) : (
          <Flex
            vertical
            align='center'
            justify='center'
            style={{ height: 'inherit' }}
          >
            <ShowMessage
              heading='Forgot Your Password?'
              text='Enter your email address and we will send you a reset link.'
              paragraphFontSize='14px'
            >
              <Flex justify='center'>
                <Form
                  form={form}
                  name='forgotPasswordForm'
                  onFinish={handleForgotPassword}
                  layout='vertical'
                  autoComplete='off'
                >
                  <Row gutter={[16, 24]}>
                    <Col span={24}>
                      <Form.Item
                        name='email'
                        rules={[
                          {
                            required: true,
                            message: 'Please enter your email address',
                          },
                          {
                            type: 'email',
                            message: 'Enter a valid email address',
                          },
                        ]}
                      >
                        <CustomInput
                          aria-label='Email address'
                          type='text'
                          placeholder='Enter your email'
                          textColor='var(--text-secondary)'
                          borderRadius='8px'
                          placeholderColor='var(--text-secondary)'
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <Form.Item>
                        <CustomButton
                          text='Send Reset Link'
                          block={true}
                          height='48px'
                          bgColor={btnColor}
                          color='#fff'
                          borderRadius='8px'
                          fontSize='16px'
                          fontWeight={500}
                          htmlType='submit'
                          isFormValid={isFormValid}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </Flex>
            </ShowMessage>
          </Flex>
        )}
      </div>
    </>
  );
};

export default ForgotPassword;
